import { useTranslation } from "react-i18next";
import {
	ATTENDANCE_STATUS_RANG,
	BulletBar,
	ChartCard,
	type Column,
	ColumnChart,
	Donut,
	HBarList,
	Histogram,
	holatVar,
	Legend,
} from "../../../../../shared/charts";
import type { AnalyticsLayoutProps } from "../../../types";
import { KpiRow, sonMatni } from "../../detail/KpiRow";
import {
	AnalyticsGrid,
	foizMatni100,
	kunYorlig,
	sanaQisqa,
	tartiblanganHaftaKunlari,
	useBolimNomi,
} from "./shared";

/** Davomat maqsadi, foiz (canvas). */
const MAQSAD = 95;
/** Hafta kunlari shkalasi — 90..100% (canvas). */
const HAFTA_MIN = 90;
const HAFTA_MAX = 100;
/** Bo'limlar bullet shkalasi va fon bandlari. */
const BOLIM_MIN = 50;
const BOLIM_BANDLARI = [80, 90];
/** Kelish gistogrammasidagi belgi — `ARRIVAL_BUCKETS` yorlig'i. */
const KELISH_BELGISI = "09:00";

/**
 * `attendance_daily` analitik ko'rinishi (reja F1):
 * KPI → [kunlar ustunlari | holatlar donuti] →
 * [kelish vaqti gistogrammasi | hafta kunlari | bo'limlar bullet ro'yxati].
 *
 * Konvert (`attendanceDaily.ts`): `daily.primary` — kelgan,
 * `daily.flagged` — kelmagan; `weekday.rate`/`byDepartment.rate` —
 * 0..100 yoki `null`; `histogram` — to'liq `ARRIVAL_BUCKETS`.
 */
export function AttendanceDailyAnalytics({ kind, data }: AnalyticsLayoutProps) {
	const { t } = useTranslation("reports");
	const bolimNomi = useBolimNomi();
	const bosh = t("analytics.attendance.empty");
	const kelganYorliq = t("analytics.attendance.daily.present");
	const kelmaganYorliq = t("analytics.attendance.daily.absent");

	const ustunlar: Column[] = data.daily.map((kun) => ({
		key: kun.date,
		label: kunYorlig(kun.date),
		stack: [
			{ value: kun.primary, color: holatVar("kelgan"), label: kelganYorliq },
			{ value: kun.flagged, color: holatVar("kelmagan"), label: kelmaganYorliq },
		],
		tooltip: `${sanaQisqa(kun.date)}: ${kelganYorliq} ${sonMatni(kun.primary)}, ${kelmaganYorliq} ${sonMatni(kun.flagged)}`,
	}));

	const holatlar = data.statusMix.map((h) => ({
		key: h.status,
		value: h.days,
		color: holatVar(ATTENDANCE_STATUS_RANG[h.status] ?? "neytral"),
		label: t(`analytics.breakdown.${h.status}`),
	}));
	const jamiKun = data.statusMix.reduce((a, h) => a + h.days, 0);

	const binlar = data.histogram.map((b) => ({ label: b.bucket, count: b.count }));
	const belgiIndeksi = binlar.findIndex((b) => b.label === KELISH_BELGISI);

	// ⚠️ `HBarList` shkalasi noldan boshlanadi — 90..100 oralig'i siljitish
	// bilan beriladi, `valueText` esa asl foizni ko'rsatadi.
	const haftaQatorlari = tartiblanganHaftaKunlari(data.weekday).map((h) => ({
		key: String(h.weekday),
		label: t(`analytics.weekday.${h.weekday}`),
		value: h.rate === null ? 0 : Math.min(HAFTA_MAX, Math.max(HAFTA_MIN, h.rate)) - HAFTA_MIN,
		valueText: foizMatni100(h.rate),
	}));

	return (
		<div data-analytics-layout="attendance_daily" className="flex flex-col gap-4">
			<KpiRow
				kind={kind}
				totals={data.totals}
				spark={data.spark}
				items={[
					{ key: "days" },
					{ key: "present" },
					{ key: "late", danger: true },
					{ key: "absent", danger: true },
				]}
			/>

			<AnalyticsGrid cols={2}>
				<ChartCard
					title={t("analytics.attendance.daily.columnsTitle")}
					subtitle={t("analytics.attendance.daily.columnsSubtitle")}
					extra={
						<Legend
							items={[
								{ label: kelganYorliq, color: holatVar("kelgan") },
								{ label: kelmaganYorliq, color: holatVar("kelmagan") },
							]}
						/>
					}
				>
					<ColumnChart columns={ustunlar} empty={bosh} />
				</ChartCard>

				<ChartCard
					title={t("analytics.attendance.daily.statusTitle")}
					subtitle={t("analytics.attendance.daily.statusSubtitle")}
				>
					{/* ⚠️ Legendada har holat qiymati bor — `uzrli` relief qoidasi. */}
					<Donut
						segments={holatlar}
						centerValue={sonMatni(jamiKun)}
						centerLabel={t("analytics.attendance.daysUnit")}
						formatValue={sonMatni}
						empty={bosh}
					/>
				</ChartCard>
			</AnalyticsGrid>

			<AnalyticsGrid cols={3}>
				<ChartCard
					title={t("analytics.attendance.daily.arrivalTitle")}
					subtitle={t("analytics.attendance.daily.arrivalSubtitle")}
				>
					<Histogram
						bins={binlar}
						marker={
							belgiIndeksi >= 0
								? { index: belgiIndeksi, label: t("analytics.attendance.daily.arrivalMarker") }
								: undefined
						}
						empty={bosh}
					/>
				</ChartCard>

				<ChartCard
					title={t("analytics.attendance.daily.weekdayTitle")}
					subtitle={t("analytics.attendance.daily.weekdaySubtitle")}
				>
					<div data-weekday-list>
						<HBarList
							rows={haftaQatorlari}
							max={HAFTA_MAX - HAFTA_MIN}
							target={MAQSAD - HAFTA_MIN}
							empty={bosh}
						/>
					</div>
				</ChartCard>

				<ChartCard
					title={t("analytics.attendance.daily.departmentsTitle")}
					subtitle={t("analytics.attendance.daily.departmentsSubtitle")}
				>
					{data.byDepartment.length === 0 ? (
						<div className="py-4 text-center text-xs" style={{ color: "var(--chart-axis)" }}>
							{bosh}
						</div>
					) : (
						<div data-department-bullets className="flex flex-col gap-2">
							{data.byDepartment.map((b) => {
								const nom = bolimNomi(b.departmentName);
								const foiz = foizMatni100(b.rate);
								return (
									<div key={b.departmentId ?? "none"} className="flex items-center gap-3">
										<div className="w-32 shrink-0 truncate text-xs" title={nom}>
											{nom}
										</div>
										<div className="min-w-0 flex-1">
											<BulletBar
												value={b.rate ?? BOLIM_MIN}
												min={BOLIM_MIN}
												max={100}
												target={MAQSAD}
												bands={BOLIM_BANDLARI}
												color={holatVar("kelgan")}
												ariaLabel={t("analytics.attendance.daily.departmentAria", {
													name: nom,
													rate: foiz,
												})}
											/>
										</div>
										<div className="w-14 shrink-0 text-right text-xs tabular-nums">{foiz}</div>
									</div>
								);
							})}
						</div>
					)}
				</ChartCard>
			</AnalyticsGrid>
		</div>
	);
}
